/**
 * Errors plugin: captures JS errors with rate limits and redaction.
 */
import type { PluginContext } from "../lib/types";
import { now } from "../lib/utils";

import { registerPlugin } from "./register";

registerPlugin("errors", (ctx: PluginContext) => {
  let windowStart = now();
  let count = 0;

  const canEmit = () => {
    if (now() - windowStart > 60000) {
      windowStart = now();
      count = 0;
    }
    if (count >= ctx.config.errors.maxPerMinute) return false;
    count += 1;
    return true;
  };

  const sanitize = (value: string | undefined) => {
    if (!value) return undefined;
    return value.length > 500 ? `${value.slice(0, 500)}...` : value;
  };

  const onError = (event: ErrorEvent) => {
    if (!canEmit()) return;
    if (!ctx.canTrackCategory("error_telemetry")) return;
    ctx.track("js_error", {
      message: sanitize(event.message),
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: ctx.config.debug ? sanitize(event.error?.stack) : undefined,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (!canEmit()) return;
    if (!ctx.canTrackCategory("error_telemetry")) return;
    const reason = event.reason;
    ctx.track("js_error", {
      message: sanitize(reason?.message || String(reason)),
      stack: ctx.config.debug ? sanitize(reason?.stack) : undefined,
    });
  };

  return {
    onInit() {
      window.addEventListener("error", onError);
      window.addEventListener("unhandledrejection", onRejection);
    },
    onUnload() {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    },
  };
});
